import React, { useEffect, useState } from "react";
import api, { inr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COLS = [["opening", "Opening"], ["earned", "Earned"], ["advances", "Advances"], ["payments", "Payments"], ["balance", "Balance"]];
const cell = "px-3 py-2 text-right num whitespace-nowrap";
const BIG_CHANGE = 0.2;

const pctChange = (cur, prev) => (prev ? (cur - prev) / Math.abs(prev) : cur ? 1 : 0);

const ChangeTag = ({ cur, prev, testid }) => {
  const diff = cur - prev;
  if (Math.abs(diff) < 0.01) return <span data-testid={testid} className="text-[11px] text-muted-foreground">no change</span>;
  const pct = pctChange(cur, prev);
  const big = Math.abs(pct) >= BIG_CHANGE;
  return (
    <span data-testid={testid} className={`text-[11px] num ${big ? "font-bold text-accent" : "text-muted-foreground"}`}>
      {diff > 0 ? "▲" : "▼"} {inr(Math.abs(diff))}{prev ? ` (${Math.round(Math.abs(pct) * 100)}%)` : ""}
    </span>
  );
};

const Comparison = ({ data }) => (
  <div data-testid="salary-compare" className="grid grid-cols-2 md:grid-cols-4 gap-3">
    {COLS.filter(([k]) => k !== "opening").map(([k, l]) => (
      <div key={k} data-testid={`compare-${k}`} className="rounded-md border border-border bg-card p-3">
        <div className="text-xs text-muted-foreground">{l}</div>
        <div className="num font-bold">{inr(data.totals[k])}</div>
        <div className="text-[11px] text-muted-foreground num">{data.prev_month}: {inr(data.prev_totals[k])}</div>
        <ChangeTag cur={data.totals[k]} prev={data.prev_totals[k]} testid={`compare-change-${k}`} />
      </div>
    ))}
  </div>
);

const RegisterTable = ({ data }) => (
  <div className="overflow-auto max-h-[60vh] border border-border rounded-md">
    <table data-testid="salary-register-table" className="w-full text-sm">
      <thead className="bg-muted sticky top-0 text-xs uppercase text-muted-foreground">
        <tr>
          <th className="px-3 py-2 text-left">Employee</th>
          <th className="px-3 py-2 text-right">Paid days</th>
          {COLS.map(([k, l]) => <th key={k} className="px-3 py-2 text-right">{l}</th>)}
          <th className="px-3 py-2 text-right">Earned vs {data.prev_month}</th>
        </tr>
      </thead>
      <tbody>
        {data.rows.map((r) => (
          <tr key={r.employee_id} data-testid={`register-row-${r.employee_id}`} className="border-t border-border">
            <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-[11px] text-muted-foreground uppercase">{r.status}</div></td>
            <td className={cell}>{r.paid_days ?? "-"}</td>
            {COLS.map(([k]) => (
              <td key={k} className={`${cell} ${k === "earned" ? "text-[hsl(var(--positive))]" : ""} ${k === "advances" || k === "payments" ? "text-destructive" : ""} ${k === "balance" ? "font-bold" : ""}`}>{inr(r[k])}</td>
            ))}
            <td className={cell}><ChangeTag cur={r.earned} prev={r.prev.earned} testid={`register-change-${r.employee_id}`} /></td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-muted font-bold">
        <tr data-testid="register-totals">
          <td className="px-3 py-2">Total ({data.rows.length})</td><td />
          {COLS.map(([k]) => <td key={k} className={cell}>{inr(data.totals[k])}</td>)}
          <td className={cell}><ChangeTag cur={data.totals.earned} prev={data.prev_totals.earned} testid="register-change-total" /></td>
        </tr>
      </tfoot>
    </table>
  </div>
);

export const SalaryRegister = ({ open, onOpenChange, month }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) { return; }
    setData(null);
    api.get("/salary/register", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Could not load salary register"); onOpenChange(false); });
  }, [open, month, onOpenChange]);

  const downloadXlsx = async () => {
    try {
      const res = await api.get("/salary/register", { params: { month, format: "xlsx" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `salary_register_${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download Excel");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="salary-register-dialog" className="max-w-5xl w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Salary Register · {month}</DialogTitle></DialogHeader>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>
        ) : <div className="space-y-4 min-w-0"><Comparison data={data} /><RegisterTable data={data} /></div>}
        <div className="flex justify-end">
          <button data-testid="salary-register-xlsx" disabled={!data} onClick={downloadXlsx}
            className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-40">
            <Download size={15} /> Download Excel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
